/*
  Controls the free-magnet / free-banner cart promo module
  (snippets/promo-free-items-panel.liquid), per PROMO-DESIGN-HANDOFF.md.

  Refresh strategy: after any cart mutation we call publish(PUB_SUB_EVENTS.cartUpdate, ...)
  with source 'promo-free-items'. Dawn's own CartItems component (assets/cart.js) already
  listens for that event and re-fetches the cart-drawer / main-cart-items sections, which is
  where this module lives - so it refreshes for free without any bespoke fetch/replace logic here.
*/
(function () {
  if (window.__vsPromoFreeItemsInit) return;
  window.__vsPromoFreeItemsInit = true;

  function cfg(panel) {
    return (window.vsPromoConfig && window.vsPromoConfig.groups && window.vsPromoConfig.groups[panel.dataset.promoGroupTag]) || {};
  }

  function routesCfg() {
    return window.vsPromoConfig || {};
  }

  function notifyCartUpdate(cartData, variantId) {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'promo-free-items', cartData: cartData, variantId: variantId });
    }
    document.dispatchEvent(new CustomEvent('cart:updated'));
  }

  async function postJSON(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    }
    return data;
  }

  function addToCart(items) {
    return postJSON(routesCfg().cartAddUrl || '/cart/add.js', { items: items });
  }

  function addToCartForm(formData) {
    return fetch(routesCfg().cartAddUrl || '/cart/add.js', { method: 'POST', body: formData }).then(async (response) => {
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || 'Something went wrong. Please try again.');
      return data;
    });
  }

  function changeLine(body) {
    return postJSON(routesCfg().cartChangeUrl || '/cart/change.js', body);
  }

  function changeLineQuantity(lineKey, quantity) {
    return changeLine({ id: lineKey, quantity: quantity });
  }

  function updateAttributes(attributes) {
    return postJSON(routesCfg().cartUpdateUrl || '/cart/update.js', { attributes: attributes });
  }

  // ---------- shared cart-state helpers (reconciliation + duplicate guards) ----------

  function updateCart(body) {
    return postJSON(routesCfg().cartUpdateUrl || '/cart/update.js', body);
  }

  // Removes a batch of cart lines (by line key) in a single /cart/update.js
  // call instead of one /cart/change.js call per line, so cleanup that spans
  // several lines/groups only triggers one Dawn section refresh.
  function removeLines(lineKeys) {
    if (!lineKeys || !lineKeys.length) return Promise.resolve(null);
    const updates = {};
    lineKeys.forEach(function (key) {
      updates[key] = 0;
    });
    return updateCart({ updates: updates });
  }

  function fetchCart() {
    const base = (routesCfg().cartUrl || '/cart').replace(/\/$/, '');
    const url = base.endsWith('.js') ? base : base + '.js';
    return fetch(url, { headers: { Accept: 'application/json' } }).then(function (response) {
      return response.json();
    });
  }

  function isBannerKind(kind) {
    return kind === 'banner' || kind === 'banner_design_service';
  }

  // A promo item "belongs" to a group if its own _promo_group property says
  // so; lines added before that property existed fall back to a variant-id
  // match, which is only unambiguous for magnets - the banner (and its
  // design-fee line) share the same product/variant across every size
  // group, so a variant-id-only fallback there can't fully disambiguate an
  // old, group-less line between two simultaneously-qualifying groups.
  function lineGroupMatches(item, tag, fallbackVariantIds) {
    const group = item.properties && item.properties._promo_group;
    if (group) return group === tag;
    const variantId = String(item.variant_id);
    return (fallbackVariantIds || []).some(function (id) {
      return id && String(id) === variantId;
    });
  }

  // Finds any banner-kind (self-design OR design-service) line, plus its
  // design-fee line if any, already in the cart for this panel's group -
  // used right before adding a new one so a method switch (or a stray
  // resubmission) replaces the old entitlement instead of stacking another
  // one alongside it.
  async function findExistingBannerLines(panel) {
    const c = cfg(panel);
    const tag = panel.dataset.promoGroupTag;
    const cart = await fetchCart();
    const keys = [];
    let hasServiceLineForGroup = false;
    cart.items.forEach(function (item) {
      const kind = item.properties && item.properties._promo_kind;
      if (!kind) return;
      if (isBannerKind(kind) && lineGroupMatches(item, tag, [c.bannerSelfDesignVariantId, c.bannerDesignServiceVariantId])) {
        keys.push(item.key);
        if (kind === 'banner_design_service') hasServiceLineForGroup = true;
      }
    });
    // The design-fee line (added by a Cart Transform Function, not by this
    // file) carries no properties of its own to match against _promo_group,
    // so it can only be scoped to this group by "this group actually has a
    // design-service banner line right now" rather than by variant id alone
    // - the fee variant is shared across groups just like the banner is.
    if (hasServiceLineForGroup && c.designServiceFeeVariantId) {
      const feeItem = cart.items.find(function (item) {
        return String(item.variant_id) === String(c.designServiceFeeVariantId);
      });
      if (feeItem) keys.push(feeItem.key);
    }
    return keys;
  }

  async function removeExistingBannerForGroup(panel) {
    const keys = await findExistingBannerLines(panel);
    return removeLines(keys);
  }

  // Magnet claims normally can't duplicate a line - repeat claims send the
  // same variant + properties, so Shopify's own cart merging just bumps the
  // quantity of the existing line. But that only holds if every claim uses
  // the exact same _promo_source_line_key; a race between two claims (double
  // -click, two tabs) that end up pointing at different source lines would
  // produce two genuinely different lines, and nothing else collapses that
  // back to one. Mirrors removeExistingBannerForGroup for the same reason.
  async function findExistingMagnetLines(panel) {
    const c = cfg(panel);
    const tag = panel.dataset.promoGroupTag;
    const cart = await fetchCart();
    return cart.items
      .filter(function (item) {
        const kind = item.properties && item.properties._promo_kind;
        return kind === 'magnet' && lineGroupMatches(item, tag, [c.magnetVariantId]);
      })
      .map(function (item) {
        return item.key;
      });
  }

  async function removeExistingMagnetForGroup(panel) {
    const keys = await findExistingMagnetLines(panel);
    return removeLines(keys);
  }

  function setBusy(panel, busy) {
    panel.classList.toggle('promo-free-items--busy', busy);
    // Global lock: an action in one group's panel disables every panel's
    // buttons, not just the acting one - now that 2-3 panels can exist at
    // once, this prevents overlapping AJAX cart mutations from racing each
    // other's async section re-renders.
    document.querySelectorAll('[data-promo-panel]').forEach(function (p) {
      p.querySelectorAll('button').forEach(function (btn) {
        btn.disabled = busy;
      });
    });
  }

  function showError(panel, message) {
    const container = panel.parentElement || panel;
    let el = container.querySelector('[data-promo-error]');
    if (!el) {
      el = document.createElement('p');
      el.className = 'promo-free-items__error';
      el.setAttribute('data-promo-error', '');
      panel.insertAdjacentElement('afterend', el);
    }
    el.textContent = message;
  }

  function bannerAttrKey(panel) {
    return cfg(panel).bannerAttributeKey || 'Free Banner Offer';
  }

  function magnetAttrKey(panel) {
    return cfg(panel).magnetAttributeKey || 'Free Magnet Offer';
  }

  async function setAttribute(key, value, notify) {
    const attrs = {};
    attrs[key] = value;
    const cartData = await updateAttributes(attrs);
    // notify defaults to true - most callers are user-initiated actions
    // (claim/decline/reconsider) that legitimately need the cart section to
    // refresh and show the new state. announceShownIfNeeded is the one
    // exception: it's pure bookkeeping with nothing new to show, so it
    // passes false to skip the full section reload that would otherwise
    // fire automatically on page load.
    if (notify !== false) notifyCartUpdate(cartData);
    return cartData;
  }

  function setBannerAttribute(panel, value, notify) {
    return setAttribute(bannerAttrKey(panel), value, notify);
  }

  function setMagnetAttribute(panel, value, notify) {
    return setAttribute(magnetAttrKey(panel), value, notify);
  }

  // ---------- magnets ----------

  async function handleClaimMagnet(panel, button) {
    const c = cfg(panel);
    const sourceEl = panel.querySelector('[data-promo-magnet-source]');
    const sourceLineKey = sourceEl ? sourceEl.value : '';

    setBusy(panel, true);
    button.disabled = true;
    try {
      // At most one free-magnet entitlement per group - see
      // findExistingMagnetLines for why this can't just rely on Shopify's
      // own cart-line merging.
      await removeExistingMagnetForGroup(panel);

      const cartData = await addToCart([
        {
          id: c.magnetVariantId,
          quantity: c.magnetQuantity || 1,
          properties: {
            _promo_kind: 'magnet',
            _promo_group: panel.dataset.promoGroupTag,
            _promo_source_line_key: sourceLineKey,
          },
        },
      ]);
      notifyCartUpdate(cartData, c.magnetVariantId);
    } catch (err) {
      showError(panel, err.message);
      button.disabled = false;
    } finally {
      setBusy(panel, false);
    }
  }

  async function handleDeclineMagnet(panel) {
    setBusy(panel, true);
    try {
      await setMagnetAttribute(panel, 'Declined');
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  async function handleReconsiderMagnet(panel) {
    setBusy(panel, true);
    try {
      await setMagnetAttribute(panel, 'Shown');
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  function toggleMagnetArtworkPicker(panel) {
    const picker = panel.querySelector('[data-promo-magnet-artwork-picker]');
    if (picker) picker.hidden = !picker.hidden;
  }

  async function handleChangeMagnetArtwork(panel, selectEl) {
    const magnetLineKey = panel.dataset.magnetLineKey;
    if (!magnetLineKey) return;
    setBusy(panel, true);
    try {
      const cartData = await changeLine({
        id: magnetLineKey,
        // changeLine's properties REPLACE the line's whole properties bag,
        // so _promo_group has to be resent here too - otherwise switching
        // artwork source would silently strip it back off the line.
        properties: {
          _promo_kind: 'magnet',
          _promo_group: panel.dataset.promoGroupTag,
          _promo_source_line_key: selectEl.value,
        },
      });
      notifyCartUpdate(cartData);
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  // ---------- banner ----------

  async function handleClaimBanner(panel, button) {
    setBusy(panel, true);
    button.disabled = true;
    try {
      await setBannerAttribute(panel, 'Claimed');
    } catch (err) {
      showError(panel, err.message);
      button.disabled = false;
    } finally {
      setBusy(panel, false);
    }
  }

  async function handleDeclineBanner(panel) {
    setBusy(panel, true);
    try {
      await setBannerAttribute(panel, 'Declined');
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  async function handleReconsiderBanner(panel) {
    setBusy(panel, true);
    try {
      await setBannerAttribute(panel, 'Shown');
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  function handleBannerOpenDesigner(panel) {
    const c = cfg(panel);
    const url =
      '/pages/custom-design-editor' +
      '?item_code=' +
      encodeURIComponent(c.bannerSelfDesignSku || '') +
      '&item_handle=' +
      encodeURIComponent(c.bannerProductHandle || '') +
      '&quantity=' +
      (c.bannerQuantity || 1) +
      '&item_id=' +
      encodeURIComponent(c.bannerSelfDesignVariantId || '') +
      '&project_id=' +
      '&promo=banner' +
      '&promo_group=' +
      encodeURIComponent(panel.dataset.promoGroupTag || '');
    window.location.href = url;
  }

  function showMethodForm(panel, selector) {
    panel.querySelectorAll('[data-promo-method-choices]').forEach(function (el) {
      el.hidden = true;
    });
    panel.querySelectorAll('[data-promo-method-form]').forEach(function (el) {
      el.hidden = el !== panel.querySelector(selector);
    });
  }

  function showMethodChoices(panel) {
    panel.querySelectorAll('[data-promo-method-form]').forEach(function (el) {
      el.hidden = true;
    });
    panel.querySelectorAll('[data-promo-method-choices]').forEach(function (el) {
      el.hidden = false;
    });
  }

  function toggleBannerUpload(panel) {
    showMethodForm(panel, '[data-promo-banner-upload-box]');
  }

  function toggleDesignServiceForm(panel) {
    showMethodForm(panel, '[data-promo-design-service-form]');
  }

  async function handleBannerUploadSubmit(panel, button) {
    const c = cfg(panel);
    const fileInput = panel.querySelector('[data-promo-banner-upload-input]');
    if (!fileInput || !fileInput.files || !fileInput.files.length) {
      showError(panel, 'Please choose an artwork file first.');
      return;
    }

    setBusy(panel, true);
    button.disabled = true;
    try {
      // At most one free banner entitlement per group - clear out any
      // earlier one (e.g. switching here after already claiming via the
      // designer) before adding this one, rather than adding alongside it.
      await removeExistingBannerForGroup(panel);

      const formData = new FormData();
      formData.append('id', c.bannerSelfDesignVariantId);
      formData.append('quantity', c.bannerQuantity || 1);
      formData.append('properties[_promo_kind]', 'banner');
      formData.append('properties[_promo_group]', panel.dataset.promoGroupTag || '');
      formData.append('properties[_promo_design_method]', 'self_design_upload');
      formData.append('properties[Uploaded Artwork]', fileInput.files[0]);

      const cartData = await addToCartForm(formData);
      notifyCartUpdate(cartData, c.bannerSelfDesignVariantId);
    } catch (err) {
      showError(panel, err.message);
      button.disabled = false;
    } finally {
      setBusy(panel, false);
    }
  }

  async function handleDesignServiceSubmit(panel, button) {
    const c = cfg(panel);
    const instructionsEl = panel.querySelector('[data-promo-design-service-instructions]');
    const filesEl = panel.querySelector('[data-promo-design-service-files]');
    const instructions = instructionsEl ? instructionsEl.value.trim() : '';

    if (!instructions) {
      showError(panel, 'Please add a few design instructions so our team knows what to create.');
      return;
    }

    setBusy(panel, true);
    button.disabled = true;
    try {
      // Same single-entitlement rule as the self-design path: switching to
      // the design service replaces any earlier free banner for this group
      // instead of adding a second one.
      await removeExistingBannerForGroup(panel);

      const formData = new FormData();
      formData.append('id', c.bannerDesignServiceVariantId);
      formData.append('quantity', c.bannerQuantity || 1);
      formData.append('properties[_promo_kind]', 'banner_design_service');
      formData.append('properties[_promo_group]', panel.dataset.promoGroupTag || '');
      formData.append('properties[Your Artwork Requirements]', instructions);
      if (filesEl && filesEl.files) {
        for (let i = 0; i < filesEl.files.length; i += 1) {
          formData.append('properties[Your Artwork ' + (i + 1) + ']', filesEl.files[i]);
        }
      }

      const bannerItem = await addToCartForm(formData);

      await setBannerAttribute(panel, 'Design Service Requested');
      notifyCartUpdate(bannerItem, c.bannerDesignServiceVariantId);
    } catch (err) {
      showError(panel, err.message);
      button.disabled = false;
    } finally {
      setBusy(panel, false);
    }
  }

  // ---------- removal modal ----------

  // Thresholds are read from the acting panel's own group config rather than
  // hardcoded, so this stays correct for every size group (they don't all
  // share the same magnet/banner threshold necessarily).
  function removeCopy(panel, kind) {
    const c = cfg(panel);
    const magnetThreshold = c.magnetThreshold || 100;
    const bannerThreshold = c.bannerThreshold || 250;
    if (kind === 'magnet') {
      return {
        title: 'Remove your free vehicle magnets?',
        body:
          "You're currently getting <b>2 free vehicle magnets</b> for qualifying with " +
          magnetThreshold +
          "+ signs. Removing this item, or dropping below " +
          magnetThreshold +
          " qualifying signs, means <b>you'll forfeit this free item</b> — it can't be added back to this order after checkout.",
        keep: 'Keep My Free Magnets',
      };
    }
    return {
      title: 'Remove your free promotional banner?',
      body:
        "You're currently getting a <b>free promotional banner</b> for qualifying with " +
        bannerThreshold +
        "+ signs, including the design work you've already completed. Removing this, or dropping below " +
        bannerThreshold +
        " qualifying signs, means <b>you'll forfeit this free item</b> — it can't be added back to this order after checkout.",
      keep: 'Keep My Free Banner',
    };
  }

  // Modals are portalled to document.body (see bindPanel) so position:fixed
  // covers the real viewport instead of being scoped by an ancestor with a
  // transform (the cart drawer's own slide-in animation, for example) - a
  // fixed-position element's containing block becomes the nearest
  // transformed ancestor instead of the viewport, which is why the modal
  // wasn't filling the page. Track panel -> modal since the modal is no
  // longer reliably reachable via panel.parentElement once moved.
  const panelModalMap = new WeakMap();

  function openRemoveModal(panel, kind) {
    const modal = panelModalMap.get(panel);
    if (!modal) return;
    // Enforce a single-open-modal invariant globally - with 2-3 panels now
    // possible at once, nothing else stops a second group's remove-modal
    // from stacking on top of an already-open one.
    document.querySelectorAll('[data-promo-remove-modal]').forEach(function (m) {
      if (m !== modal) m.hidden = true;
    });
    const copy = removeCopy(panel, kind);
    modal.querySelector('[data-promo-modal-title]').textContent = copy.title;
    modal.querySelector('[data-promo-modal-body]').innerHTML = copy.body;
    modal.querySelector('[data-promo-modal-keep]').textContent = copy.keep;
    modal.dataset.promoRemoveKind = kind;
    modal.hidden = false;
  }

  function closeRemoveModal(modal) {
    modal.hidden = true;
    delete modal.dataset.promoRemoveKind;
  }

  async function performRemove(panel, modal, kind) {
    const lineKey = kind === 'magnet' ? panel.dataset.magnetLineKey : panel.dataset.bannerLineKey;
    const feeLineKey = panel.dataset.designFeeLineKey;
    if (!lineKey) {
      closeRemoveModal(modal);
      return;
    }
    setBusy(panel, true);
    try {
      let cartData = await changeLineQuantity(lineKey, 0);
      if (kind === 'banner' && feeLineKey) {
        cartData = await changeLineQuantity(feeLineKey, 0);
      }
      closeRemoveModal(modal);
      notifyCartUpdate(cartData);
    } catch (err) {
      showError(panel, err.message);
    } finally {
      setBusy(panel, false);
    }
  }

  // ---------- collapse / expand ----------

  function collapseModule(container) {
    const full = container.querySelector('[data-promo-panel]');
    const pill = container.querySelector('[data-promo-collapsed-pill]');
    if (full) full.hidden = true;
    if (pill) pill.hidden = false;
  }

  function expandModule(container) {
    const full = container.querySelector('[data-promo-panel]');
    const pill = container.querySelector('[data-promo-collapsed-pill]');
    if (full) full.hidden = false;
    if (pill) pill.hidden = true;
  }

  // ---------- housekeeping ----------

  // Reads the always-rendered per-group marker from promo-free-items-config.liquid
  // (see that file for why this can't just come from window.vsPromoConfig -
  // that object is set from a <script> tag that Dawn's innerHTML-based
  // section refresh doesn't re-execute, so it goes stale after any AJAX-only
  // cart mutation; this plain HTML attribute does not).
  function currentEligibleQty(tag) {
    const el = document.querySelector('[data-promo-group-status][data-promo-group-tag="' + tag + '"]');
    return el ? parseInt(el.dataset.eligibleQty || '0', 10) : null;
  }

  // ---------- global promo reconciliation ----------
  //
  // Replaces the old panel-dependent pruneDroppedItems(): that function only
  // ever ran from inside bindPanel(), which only runs for panels that exist
  // in the DOM - and promo-free-items-panel.liquid stops rendering a group's
  // panel entirely once its eligible quantity hits 0. So the one case that
  // most needs cleanup (a qualifying product removed/replaced entirely) was
  // exactly the case where cleanup could never run. This works off the live
  // cart JSON instead, so it doesn't depend on any panel still existing.
  //
  // In-flight guard: reconciliation itself mutates the cart, which re-fires
  // the same cart:updated / PUB_SUB_EVENTS.cartUpdate signals that trigger
  // it - promoReconcileInFlight prevents overlapping runs from racing each
  // other's /cart/update.js calls, and promoReconcilePending makes sure a
  // mutation that arrives mid-run isn't silently dropped (it queues exactly
  // one follow-up pass instead). The chain always terminates: a follow-up
  // pass over an already-consistent cart finds nothing left to fix, so it
  // never calls notifyCartUpdate, and nothing re-triggers a further pass.
  let promoReconcileInFlight = false;
  let promoReconcilePending = false;

  async function reconcilePromoItems() {
    if (promoReconcileInFlight) {
      promoReconcilePending = true;
      return;
    }

    const groups = (window.vsPromoConfig && window.vsPromoConfig.groups) || {};
    const groupTags = Object.keys(groups);
    // No status markers rendered yet (cart-drawer/main-cart-items haven't
    // loaded) - nothing reliable to validate against yet, so do nothing
    // rather than risk treating "not loaded" as "doesn't qualify".
    if (!groupTags.length || !document.querySelector('[data-promo-group-status]')) return;

    promoReconcileInFlight = true;
    try {
      const cart = await fetchCart();
      const removeKeys = [];
      const attributeResets = {};

      groupTags.forEach(function (tag) {
        const c = groups[tag];
        const eligibleQty = currentEligibleQty(tag);
        if (eligibleQty === null) return; // this group has no marker in this render pass

        const magnetOk = eligibleQty >= (c.magnetThreshold || 100);
        const bannerOk = !!c.hasBanner && eligibleQty >= (c.bannerThreshold || 250);
        let keptMagnetForGroup = false;
        let keptBannerForGroup = false;
        let serviceBannerKeptForGroup = false;

        cart.items.forEach(function (item) {
          const kind = item.properties && item.properties._promo_kind;
          if (!kind) return;

          if (kind === 'magnet') {
            if (!lineGroupMatches(item, tag, [c.magnetVariantId])) return;
            const sourceKey = item.properties._promo_source_line_key;
            const sourceOk = !sourceKey || cart.items.some(function (i) { return i.key === sourceKey; });
            // Duplicate protection: normally Shopify merges repeat magnet
            // claims into one line automatically (same variant + properties),
            // but two claims that raced to different source lines wouldn't
            // merge - keep at most one line per group here too.
            if (!magnetOk || !sourceOk || keptMagnetForGroup) {
              removeKeys.push(item.key);
            } else {
              keptMagnetForGroup = true;
            }
            return;
          }

          if (isBannerKind(kind)) {
            if (!lineGroupMatches(item, tag, [c.bannerSelfDesignVariantId, c.bannerDesignServiceVariantId])) return;
            // Duplicate protection: a group may only ever keep one banner
            // entitlement (self-design OR design-service), no matter how it
            // got duplicated - reopening the designer, browser back, a
            // double-click, or switching methods. Keep whichever one is
            // encountered first and drop the rest.
            if (!bannerOk || keptBannerForGroup) {
              removeKeys.push(item.key);
            } else {
              keptBannerForGroup = true;
              if (kind === 'banner_design_service') serviceBannerKeptForGroup = true;
            }
          }
        });

        // The design-fee line is added by a Cart Transform Function, not by
        // this file, so it carries no properties to match against
        // _promo_group - it can only be scoped to this group by "this
        // group still has a kept design-service banner line", since the fee
        // variant is shared across groups the same way the banner is.
        if (c.designServiceFeeVariantId) {
          const feeItem = cart.items.find(function (i) { return String(i.variant_id) === String(c.designServiceFeeVariantId); });
          if (feeItem && (!bannerOk || !serviceBannerKeptForGroup)) removeKeys.push(feeItem.key);
        }

        // A deliberate "Declined" is a customer decision, not progress state
        // left behind by crossing a threshold - only clear the transient
        // in-flight values (Shown / Claimed / Design Service Requested) that
        // no longer make sense once the group stops qualifying.
        const magnetAttrValue = cart.attributes && cart.attributes[c.magnetAttributeKey];
        if (!magnetOk && magnetAttrValue && magnetAttrValue !== 'Declined') {
          attributeResets[c.magnetAttributeKey] = '';
        }
        const bannerAttrValue = cart.attributes && cart.attributes[c.bannerAttributeKey];
        if (!bannerOk && bannerAttrValue && bannerAttrValue !== 'Declined') {
          attributeResets[c.bannerAttributeKey] = '';
        }
      });

      const uniqueRemoveKeys = removeKeys.filter(function (key, index) {
        return removeKeys.indexOf(key) === index;
      });

      if (uniqueRemoveKeys.length || Object.keys(attributeResets).length) {
        const body = {};
        if (uniqueRemoveKeys.length) {
          body.updates = {};
          uniqueRemoveKeys.forEach(function (key) {
            body.updates[key] = 0;
          });
        }
        if (Object.keys(attributeResets).length) {
          body.attributes = attributeResets;
        }
        // One combined call for every removal + attribute reset across every
        // group, and one notifyCartUpdate - not one per group.
        const cartData = await updateCart(body);
        notifyCartUpdate(cartData);
      }
    } catch (err) {
      console.error('Promo reconciliation failed:', err);
    } finally {
      promoReconcileInFlight = false;
      if (promoReconcilePending) {
        promoReconcilePending = false;
        reconcilePromoItems();
      }
    }
  }

  async function announceShownIfNeeded(panel) {
    const magnetThreshold = parseInt(panel.dataset.magnetThreshold || '0', 10);
    const bannerThreshold = parseInt(panel.dataset.bannerThreshold || '0', 10);
    const eligibleQty = parseInt(panel.dataset.eligibleQty || '0', 10);

    try {
      if (eligibleQty >= magnetThreshold && !panel.dataset.magnetAttribute) {
        await setMagnetAttribute(panel, 'Shown', false);
      }
      if (eligibleQty >= bannerThreshold && !panel.dataset.bannerAttribute) {
        await setBannerAttribute(panel, 'Shown', false);
      }
    } catch (err) {
      console.error(err);
    }
  }

  function syncCheckoutGate() {
    const bannerRequired = !!document.querySelector('[data-promo-panel][data-promo-banner-required]');
    document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach(function (btn) {
      if (bannerRequired) {
        if (!btn.dataset.promoOriginalText) {
          btn.dataset.promoOriginalText = btn.textContent.trim();
        }
        btn.disabled = true;
        btn.textContent = 'Checkout — complete banner choice first';
        btn.classList.add('promo-checkout-blocked');
      } else if (btn.classList.contains('promo-checkout-blocked')) {
        btn.disabled = false;
        btn.textContent = btn.dataset.promoOriginalText || btn.textContent;
        btn.classList.remove('promo-checkout-blocked');
      }
    });
  }

  function bindPanel(panel) {
    if (panel.dataset.promoBound === 'true') return;
    panel.dataset.promoBound = 'true';

    const container = panel.parentElement;

    panel.addEventListener('click', function (event) {
      const t = event.target;

      const claimMagnetBtn = t.closest('[data-promo-add-magnet]');
      if (claimMagnetBtn) return handleClaimMagnet(panel, claimMagnetBtn);

      const magnetDeclineBtn = t.closest('[data-promo-magnet-decline]');
      if (magnetDeclineBtn) return handleDeclineMagnet(panel);

      const changeArtworkBtn = t.closest('[data-promo-magnet-change-artwork]');
      if (changeArtworkBtn) return toggleMagnetArtworkPicker(panel);

      const claimBannerBtn = t.closest('[data-promo-claim-banner]');
      if (claimBannerBtn) return handleClaimBanner(panel, claimBannerBtn);

      const bannerDeclineBtn = t.closest('[data-promo-banner-decline]');
      if (bannerDeclineBtn) return handleDeclineBanner(panel);

      const openDesignerBtn = t.closest('[data-promo-banner-open-designer]');
      if (openDesignerBtn) return handleBannerOpenDesigner(panel);

      const uploadToggleBtn = t.closest('[data-promo-banner-upload-toggle]');
      if (uploadToggleBtn) return handleBannerOpenDesigner(panel);

      const uploadSubmitBtn = t.closest('[data-promo-banner-upload-submit]');
      if (uploadSubmitBtn) return handleBannerUploadSubmit(panel, uploadSubmitBtn);

      const serviceToggleBtn = t.closest('[data-promo-banner-service-toggle]');
      if (serviceToggleBtn) return toggleDesignServiceForm(panel);

      const serviceSubmitBtn = t.closest('[data-promo-design-service-submit]');
      if (serviceSubmitBtn) return handleDesignServiceSubmit(panel, serviceSubmitBtn);

      const methodBackBtn = t.closest('[data-promo-method-back]');
      if (methodBackBtn) return showMethodChoices(panel);

      const removeBtn = t.closest('[data-promo-remove]');
      if (removeBtn) return openRemoveModal(panel, removeBtn.dataset.promoRemove);

      const collapseBtn = t.closest('[data-promo-collapse-toggle]');
      if (collapseBtn) return collapseModule(container);
    });

    panel.addEventListener('change', function (event) {
      const sourceChange = event.target.closest('[data-promo-magnet-source-change]');
      if (sourceChange) handleChangeMagnetArtwork(panel, sourceChange);
    });

    // Header "Add magnets/banner instead" reconsider links.
    container.addEventListener('click', function (event) {
      const t = event.target;
      const magnetReconsider = t.closest('[data-promo-magnet-reconsider]');
      if (magnetReconsider) return handleReconsiderMagnet(panel);
      const bannerReconsider = t.closest('[data-promo-banner-reconsider]');
      if (bannerReconsider) return handleReconsiderBanner(panel);
      const expandBtn = t.closest('[data-promo-expand-toggle]');
      if (expandBtn) return expandModule(container);
    });

    // The header bar and collapsed pill are whole-element toggles
    // (role="button" tabindex="0" in the Liquid, matching the PDP pill's
    // <summary> pattern) rather than a small nested <button> - support
    // Enter/Space the same way a native button would.
    container.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const toggle = event.target.closest('[data-promo-collapse-toggle], [data-promo-expand-toggle]');
      if (!toggle) return;
      event.preventDefault();
      toggle.click();
    });

    const modal = container.querySelector('[data-promo-remove-modal]');
    if (modal) {
      // Each cart refresh renders a fresh modal node inside the swapped-in
      // HTML; drop the previous portalled one for this same context (drawer
      // vs cart page, AND same group - with 2-3 panels now possible at once,
      // owner must be panel-specific or binding panel #2 would delete panel
      // #1's already-portalled modal out from under it) so they don't pile
      // up in document.body indefinitely.
      const owner = (panel.closest('cart-drawer-items') ? 'drawer' : 'page') + ':' + panel.dataset.promoGroupTag;
      document.querySelectorAll('body > [data-promo-remove-modal][data-promo-modal-owner="' + owner + '"]').forEach(
        function (old) {
          if (old !== modal) old.remove();
        }
      );
      modal.setAttribute('data-promo-modal-owner', owner);
      document.body.appendChild(modal);
      panelModalMap.set(panel, modal);

      if (modal.dataset.promoBound !== 'true') {
        modal.dataset.promoBound = 'true';
        modal.addEventListener('click', function (event) {
          if (event.target === modal) return closeRemoveModal(modal);
          if (event.target.closest('[data-promo-modal-keep]')) return closeRemoveModal(modal);
          if (event.target.closest('[data-promo-modal-remove]')) {
            return performRemove(panel, modal, modal.dataset.promoRemoveKind);
          }
        });
      }
    }

    announceShownIfNeeded(panel);
  }

  function bindAllPanels() {
    document.querySelectorAll('[data-promo-panel]').forEach(bindPanel);
    syncCheckoutGate();
    // Cart-level cleanup runs globally on every bind pass (initial load,
    // every cart mutation) instead of per-panel - see reconcilePromoItems
    // above for why this replaced the old per-panel pruneDroppedItems.
    reconcilePromoItems();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllPanels, { once: true });
  } else {
    bindAllPanels();
  }

  // Dawn replaces cart-drawer / main-cart-items wholesale via an async fetch
  // after any cart mutation (its own quantity stepper included, not just ours).
  // A fixed setTimeout here was a race: the fetch can easily take longer than
  // the guessed delay, so handlers would get attached to a node about to be
  // discarded, leaving the real (new) buttons dead. Watching the actual
  // container for the DOM swap instead of guessing its timing avoids that.
  function debounce(fn, ms) {
    let t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, ms);
    };
  }

  const debouncedRebind = debounce(bindAllPanels, 30);

  function observeCartContainers() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems) {
      new MutationObserver(debouncedRebind).observe(cartItems, { childList: true, subtree: true });
    }
    const cartDrawer = document.getElementById('CartDrawer');
    if (cartDrawer) {
      new MutationObserver(debouncedRebind).observe(cartDrawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCartContainers, { once: true });
  } else {
    observeCartContainers();
  }

  // Keep these as a fast-path fallback (harmless - bindPanel/syncCheckoutGate are idempotent).
  document.addEventListener('cart:updated', bindAllPanels);
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, debouncedRebind);
  }
})();
